/*
 * One validator per kind of search param. Each takes whatever the router's
 * search parser yields (it reads every value as JSON first, so `?page=2` is the
 * number 2 and `?page="2"` the string) and answers the value, or undefined so
 * the param is dropped rather than forwarded: a value that cannot be what the
 * param names would only spend a read to be refused, and an attacker-shaped
 * string would ride into the request.
 */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DIGITS_RE = /^\d+$/;
const TEXT_MAX = 100;
const YEAR_MIN = 2000;
const YEAR_MAX = 9999;

/** An integer from a number, or from a string of digits. */
function integerOf(value: unknown): number | undefined {
  if (typeof value === "number") return Number.isSafeInteger(value) ? value : undefined;
  if (typeof value !== "string" || !DIGITS_RE.test(value)) return undefined;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : undefined;
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

export function uuidParam(value: unknown): string | undefined {
  return typeof value === "string" && UUID_RE.test(value) ? value : undefined;
}

export function enumParam<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return allowed.find((member) => member === value);
}

/** A real calendar day written `YYYY-MM-DD`: `2026-02-30` is no day and is dropped. */
export function dateParam(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const match = DATE_RE.exec(value);
  if (!match) return undefined;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1 || month < 1 || month > 12 || day < 1) return undefined;
  return day <= daysInMonth(year, month) ? value : undefined;
}

/** A page past the first; the first page is the param's absence. */
export function pageParam(value: unknown): number | undefined {
  const page = integerOf(value);
  return page !== undefined && page >= 2 ? page : undefined;
}

export function yearParam(value: unknown): number | undefined {
  const year = integerOf(value);
  return year !== undefined && year >= YEAR_MIN && year <= YEAR_MAX ? year : undefined;
}

/**
 * Search text: trimmed, cut to 100 characters (counted as characters, so an
 * emoji is never split into a half the URL cannot encode), and dropped when
 * nothing is left. A number is the text the person typed: the router parsed
 * `?q=2231904` as JSON.
 */
export function textParam(value: unknown): string | undefined {
  const text =
    typeof value === "string" ? value : Number.isFinite(value) ? String(value) : undefined;
  if (text === undefined) return undefined;
  const cut = Array.from(text.trim()).slice(0, TEXT_MAX).join("").trimEnd();
  return cut.length > 0 ? cut : undefined;
}
